/*
 * @FilePath: /kitchen-online-web/src/3DEngine/render/renderEngine/index.js
 * @Author: Yongchao Wang
 * @Date: 2021-07-20 10:32:44
 * @LastEditors: Dai Guo
 * @LastEditTime: 2022-06-22 09:26:15
 */

import { Vector3 } from '@babylonjs/core/Maths/math';
import { Tools } from '@babylonjs/core/Misc/index';

import '@babylonjs/loaders';
import { ArcRotateCamera, Engine } from '@babylonjs/core';
import RenderIType from '../renderTypes/renderIType';
import RenderLType from '../renderTypes/renderLType/renderLType';
import RenderUType from '../renderTypes/renderUType/renderUType';
import RenderHType from '../renderTypes/renderHType/renderHType';

import {
  toDomPos,
  compChangeDoorMap,
  getHightFrameLengthByPosition,
  // compressImg,
} from '../utils/renderUtils';
import {
  getDoorsAndHighFrameDoors, getSidePlates, getSealPlates, getCornerPlates,
} from '../../callEngine/processedData';
import { BASICTYPE_ENUM, LengthType } from '../utils/enum';
import HightFrame from '../specialHandler/highFrame';
import { VOXTORPGLOBALNAME, TOPHIGHTFRAMESEPARATEDLENGTH } from '../utils/kitchenConfig';
import SidePlate from '../specialHandler/sidePlate';
import SealPlate from '../specialHandler/sealPlate';
import CornerCabinetPlate from '../specialHandler/cornerCabinetPlate';
import calloutLineRender from '../../components/calloutLineCanvas/calloutLinesRender';
import RotateHideMesh from '../specialHandler/hideMesh';
import BaseRenderEngine from './baseRenderEngine';
import { handleJson } from '../utils/templatejsonUtils';
import FloorShadow from '../specialHandler/floorShadow';

class RenderEngine extends BaseRenderEngine {
  XNeedHideMeshIdList = [];

  P2XNeedHideMeshIdList = [];

  renderSubscribe=null

  isRunning = false;

  setMeshArray(meshArray) {
    this.meshArray = meshArray;
  }

  setCameraTarget(target) {
    this.camera.setTarget(new Vector3(parseFloat(target.x), parseFloat(target.y), parseFloat(target.z)));
  }

  setCameraPosition(position) {
    this.camera.setPosition(new Vector3(parseFloat(position.x), parseFloat(position.y), parseFloat(position.z)));
  }

  getCameraPosition() {
    return this.camera.position;
  }

  getCameraTarget() {
    return this.camera.target;
  }

  // 设置需要显隐的id
  setMeshIdList(templateJson) {
    this.XNeedHideMeshIdList = handleJson(templateJson).XNeedHideMeshIdList;
    this.P2XNeedHideMeshIdList = handleJson(templateJson).P2XNeedHideMeshIdList;
    this.renderSubscribe?.dispatch(this.camera, true);
  }

  /**
   * @description: 异步初始化的配置包括底柜柜型、长度123、高度、顶柜柜型、长度123、高度
   * @param {*} templateJson 链接地址
   * https://confluence.build.ingka.ikea.com/pages/viewpage.action?spaceKey=Kitchen&title=kitchen-json-template
   * @return {*}
   * @author: Yongchao Wang
   */
  async initConfig(templateJson, callback) {
    if (this.isRendering) { return; }

    this.templateJson = templateJson;
    // 后续处理冰箱的时候会使用pop方法改变template所以需要copy一份  隐藏的时候时候使用
    this.copyTemplateJson = JSON.parse(JSON.stringify(templateJson));
    this.calloutLineObj = {
      bottom: {
        length1: [],
        length2: [],
        length3: [],
      },
      top: {
        length1: [],
        length2: [],
        length3: [],
      },
    };
    this.hideL1calloutLines = false;
    this.hideL3calloutLines = false;
    this.scene.meshes = [];
    // if (this.bottomRenderEngine) {
    //   this.bottomRenderEngine.clearAllMeshed();
    // }
    // // version2.0  当有顶柜模型的时候才将顶柜模型全部清除
    // if (this.topRenderEngine) { this.topRenderEngine.clearAllMeshed(); }
    // version2.0 当顶柜为null的时候不进行顶柜的模型加载
    let assets = this.templateJson.top ? [
      templateJson?.bottom?.length1?.frames,
      templateJson?.bottom?.length2?.frames,
      templateJson?.bottom?.length3?.frames,
      templateJson?.top?.length1?.frames,
      templateJson?.top?.length2?.frames,
      templateJson?.top?.length3?.frames,
    ] : [
      templateJson?.bottom?.length1?.frames,
      templateJson?.bottom?.length2?.frames,
      templateJson?.bottom?.length3?.frames,
    ];
    assets = [...assets, ...SidePlate.getSidePlatesAssets(templateJson),
      ...SealPlate.getSealPlatesAssets(templateJson),
      ...CornerCabinetPlate.getCornerCabinetPlateMesh(templateJson),
    ];
    const {
      error,
    } = await this.loadAsset(assets);
    if (!error.length) {
      this.initRenderType(templateJson);


      this.bottomRenderEngine.windowVisiable = this.windowVisiable;
      this.bottomRenderEngine.renderBottom();
      // version2.0 无顶柜的时候不进行顶柜的渲染
      if (this.templateJson.top) { this.topRenderEngine.renderTop(); }
      // version2.0 add by zhanyu.shen 20211228, 单独渲染踢脚板，顶柜有高柜影响踢脚板
      if (this.templateJson.bottom) { this.bottomRenderEngine.renderSkirtingBoard(); }
      // version2.0 判断有无顶柜的渲染器，有的话设置位置的时候包括顶柜没有的话就只有底柜


      if (['H', 'U', 'U_S'].includes(templateJson.bottom.type)) {
        // eslint-disable-next-line no-new
        const floorShadow = new FloorShadow(templateJson, this.scene, this.bottomRenderEngine);
        floorShadow.initShadow();
      }
      let allMesh = [];
      if (this.topRenderEngine) {
        allMesh = [...this.bottomRenderEngine.allMeshes, ...this.topRenderEngine?.allMeshes];
      } else {
        allMesh = this.bottomRenderEngine.allMeshes;
      }

      if (this.isShowRoom) {
        this?.bottomRenderEngine?.renderRoom(this.copyTemplateJson);
      }

      const {
        target, position, lowerAlphaLimit, upperAlphaLimit, beta, // alpha,
      } = this.bottomRenderEngine.getCameraBestConfig(this.camera.fov,
        allMesh, this.camera);
      this.initTarget = target; // 记录初始原点位置
      this.initPositiont = position; // 记录相机初始位置
      this.camera.setTarget(target);
      this.camera.setPosition(position);
      const distance = Vector3.Distance(target, position);
      this.camera.upperRadiusLimit = distance;
      this.camera.lowerRadiusLimit = distance;

      if (['H', 'U', 'U_S'].includes(templateJson.bottom.type)) {
        this.renderSubscribe = new RotateHideMesh(this.copyTemplateJson,
          this.scene, this.bottomRenderEngine, this.camera, distance);
        this.camera.onViewMatrixChangedObservable.add(() => {
          this.hideL1calloutLines = this.renderSubscribe.hideL1calloutLines;
          this.hideL3calloutLines = this.renderSubscribe.hideL3calloutLines;
          this.renderSubscribe.dispatch();
        });
      }

      await this.showAllMesh();
      setTimeout(async () => {
        // 设置距离焦点的距离大小，限制放大倍数
        if (process.env.NODE_ENV !== 'development') {
          this.camera.lowerAlphaLimit = lowerAlphaLimit;
          this.camera.upperAlphaLimit = upperAlphaLimit;
          // if (alpha) {
          //   // this.camera.alpha = alpha;
          //   console.log(alpha);
          // }
          if (beta) { this.camera.beta = beta; }
          this.camera.upperBetaLimit = this.camera.beta;
          this.camera.lowerBetaLimit = this.camera.beta - 30 * (Math.PI / 180);

          this.camera.lowerRadiusLimit = distance * 0.6;
        }
        await this.retryGetCalloutLines();
        this.engine.hideLoadingUI();
        callback({
          target,
          position,
        });
      }, 1000);
    } else {
      callback();
      this.isRendering = false;
    }
  }

  /**
   * @description: 有些情况渲染慢，未拿到标线的位置，重复去get，直到拿到point
   * @param {*}
   * @return {*}
   * @author: Yongchao Wang
   */
  retryGetCalloutLines() {
    this.bottomRenderEngine.setCalloutLine();
    // version2.0 无顶柜的时候不进行顶柜的标线的渲染
    if (this.templateJson.top) this.topRenderEngine.setCalloutLine();
    this.renderCalloutLines('bottom');
    // version2.0 无顶柜的时候不进行顶柜的标线的渲染
    if (this.templateJson.top) this.renderCalloutLines('top');
    this.calloutLineObj.renderType = this.templateJson.bottom.type;
  }

  async showAllMesh() {
    await new Promise((resolve) => {
      this.isRendering = true;
      this.isRunning = true;
      this.engine.runRenderLoop(() => {
        this.scene.render();
      });
      setTimeout(() => {
        this.isRendering = false;
        resolve();
      }, 100);
    });
  }

  renderCalloutLines(key) {
    // version2.0 判断有无顶柜   无顶柜的话只有底柜的标线  不去添加顶柜的标线
    if (Object.keys(this.templateJson).length === 0 || !this.bottomRenderEngine) {
      return;
    }
    const calloutLines = this.templateJson.top
      ? [this.bottomRenderEngine.getCalloutLineInfo(), this.topRenderEngine.getCalloutLineInfo()]
      : [this.bottomRenderEngine.getCalloutLineInfo()];
    calloutLines.forEach((calloutLine) => {
      Object.keys(calloutLine[key]).forEach((length) => {
        if (calloutLine[key][length]) {
          const calloutLineArr = calloutLine[key][length].map((lineInfo) => {
            const firstPoint = toDomPos(lineInfo.line.startPoint, this.scene, this.camera, this.engine);
            const secondPoint = toDomPos(lineInfo.line.endPoint, this.scene, this.camera, this.engine);
            let show = true;
            if ((this.hideL1calloutLines && length === LengthType.Length1)
              || (this.hideL3calloutLines && length === LengthType.Length3)) {
              show = false;
            }
            return {
              firstPoint,
              secondPoint,
              title: lineInfo.title,
              content: lineInfo.type === 'length' ? lineInfo.value : lineInfo.value * 100,
              isHeight: lineInfo.isHeight,
              show,
            };
          });
          this.calloutLineObj[key][length] = calloutLineArr;
        }
      });
    });
  }


  /**
   * @description: 初始化渲染器
   * @param {*} templateJson
   * @return {*}
   * @author: Yongchao Wang
   */
  initRenderType(templateJson) {
    const bottomJson = JSON.parse(JSON.stringify(templateJson));
    const topJson = JSON.parse(JSON.stringify(templateJson));
    switch (templateJson.bottom.type) {
      case 'I':
        this.bottomRenderEngine = new RenderIType(bottomJson, this.scene, this.engine, true, this.camera);
        break;
      case 'L':
        this.bottomRenderEngine = new RenderLType(bottomJson, this.scene, this.engine, true, this.camera);
        break;
      case 'U':
      case 'U_S':
        this.bottomRenderEngine = new RenderUType(bottomJson, this.scene, this.engine, true, this.camera);
        break;
      default:
        this.bottomRenderEngine = new RenderHType(bottomJson, this.scene, this.engine, true, this.camera);
        break;
    }

    // version2.0 判断有无顶柜 top不为null的时候才进行顶柜的渲染
    if (templateJson.top) {
      switch (templateJson.top.type) {
        case 'I':
          this.topRenderEngine = new RenderIType(topJson, this.scene, this.engine, false, this.camera);
          break;
        case 'L':
          this.topRenderEngine = new RenderLType(topJson, this.scene, this.engine, false, this.camera);
          break;
        default:
          this.topRenderEngine = new RenderHType(topJson, this.scene, this.engine, false, this.camera);
          break;
      }
    }
  }


  /**
   * @description: 改变门板
   * @param {*} templateJson 完成的templateJson
   * @param {*} oldTemplatejson 老oldTemplatejson
   * @param {*} bottomChangeMap 改变的门板内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @param {*} topChangeMap 改变的门板内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @param {*} skirtingBoard 踢脚板
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeDoors(templateJson, oldTemplatejson, bottomChangeMap, topChangeMap, callback, skirtingBoard) {
    await this.loadAsset([bottomChangeMap?.new, topChangeMap?.new]);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    if (bottomChangeMap) {
      this.bottomRenderEngine.replaceMeshes(templateJson, bottomChangeMap);
      this.bottomRenderEngine.templateJson = templateJson;
    }
    // version2.0   判断无顶柜状态
    if (topChangeMap && this.topRenderEngine) {
      this.topRenderEngine.replaceMeshes(templateJson, {
        ...topChangeMap,
        type: templateJson.top?.type,
        position: 'top',
      });

      this.topRenderEngine.templateJson = templateJson;
    }

    if (skirtingBoard) {
      this.bottomRenderEngine.changeSkritingTexture(skirtingBoard);
    }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 改变门板
   * @param {*} templateJson 完成的templateJson
   * @param {*} oldTemplatejson 老oldTemplatejson
   * @param {*} bottomChangeMap 改变的门板内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @param {*} topChangeMap 改变的门板内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @param {*} skirtingBoard 踢脚板
   * @param {*} framePosition 改变柜的位置，top、bottom
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeDoorsWithVoxtorp(templateJson, oldTemplatejson, callback, skirtingBoard, framePosition) {
    const newDoors = getDoorsAndHighFrameDoors(templateJson);
    const oldDoors = getDoorsAndHighFrameDoors(oldTemplatejson);
    const allNewDoors = getDoorsAndHighFrameDoors(templateJson);
    const allOldDoors = getDoorsAndHighFrameDoors(oldTemplatejson);

    allNewDoors.top = [
      ...allNewDoors.top,
      ...getSidePlates(templateJson, framePosition).top,
      ...getSealPlates(templateJson).top,
    ];
    allNewDoors.bottom = [
      ...allNewDoors.bottom,
      ...getSidePlates(templateJson).bottom,
      ...getSealPlates(templateJson).bottom,
      ...getCornerPlates(templateJson),
    ];
    allOldDoors.top = [
      ...allOldDoors.top,
      ...getSidePlates(oldTemplatejson, framePosition).top,
      ...getSealPlates(oldTemplatejson).top,
    ];
    allOldDoors.bottom = [
      ...allOldDoors.bottom,
      ...getSidePlates(oldTemplatejson).bottom,
      ...getSealPlates(oldTemplatejson).bottom,
      ...getCornerPlates(oldTemplatejson),
    ];
    const bottomChangeMap = { old: allOldDoors.bottom, new: allNewDoors.bottom };
    const topChangeMap = { old: allOldDoors.top, new: allNewDoors.top };

    const topDeafultChangeMap = { old: oldDoors.top, new: newDoors.top };
    const bottomHighFrameChangeMap = {
      old: allOldDoors.bottomHighFrame,
      new: allNewDoors.bottomHighFrame,
    };
    const topHighFrameChangeMap = { old: allOldDoors.topHighFrame, new: allNewDoors.topHighFrame };
    let assets = [];
    if (framePosition === 'top') {
      assets = [topChangeMap?.new];
    } else {
      assets = [
        bottomChangeMap?.new,
        bottomHighFrameChangeMap?.new,
        topChangeMap?.new,
        topHighFrameChangeMap?.new,
      ];
    }
    await this.loadAsset(assets);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    if (framePosition === 'bottom') {
      this.bottomRenderEngine.templateJson = templateJson;

      this.bottomRenderEngine.replaceMeshes(templateJson, bottomChangeMap);
      // 只有在改底柜柜门时候，才会改高柜柜门
      if (newDoors.inBottomHighFrame.length) {
        newDoors.inBottomHighFrame.forEach((frame, index) => {
          // 门板是获取所有的高柜，针对单个柜体需要拆分
          const doorchangeMap = compChangeDoorMap(newDoors.inBottomHighFrame, bottomHighFrameChangeMap, index);
          const highFrameInt = new HightFrame(frame, this.scene);
          highFrameInt.reRenderHighFrameAccessories(doorchangeMap, this.bottomRenderEngine, 'bottom');
        });
      }
    }
    // version2.0   判断无顶柜状态
    if (topChangeMap && this.topRenderEngine) {
      this.topRenderEngine.templateJson = templateJson;


      this.topRenderEngine.replaceMeshes(templateJson, {
        ...topChangeMap,
        type: templateJson.top?.type,
        position: 'top',
      });
      // DONE 高柜加在顶柜，需要根据新的字段取判断，使用传入的highFrame换沃拓普会异常
      // 只有在改底柜柜门时候，才会改高柜柜门
      if (framePosition === 'bottom' && newDoors.inTopHighFrame.length) {
        newDoors.inTopHighFrame.forEach((frame, index) => {
          // 门板是获取所有的高柜，针对单个柜体需要拆分
          const doorchangeMap = compChangeDoorMap(newDoors.inTopHighFrame, topHighFrameChangeMap, index);
          const highFrameInt = new HightFrame(frame, this.scene);
          highFrameInt.reRenderHighFrameAccessories(doorchangeMap, this.topRenderEngine, 'top');
        });
        // const highFrameInt = new HightFrame(newDoors.inTopHighFrame, this.scene);
        // highFrameInt.reRenderHighFrameAccessories(topHighFrameChangeMap, this.topRenderEngine, 'top');
      }
      // version2.0   顶柜沃拓普门板旋转
      if (templateJson?.top?.globalName === VOXTORPGLOBALNAME) {
        // 老的是其它门板，新的是沃拓普门板，需要旋转上移一定距离
        const needMoveUp = oldTemplatejson.top.globalName !== templateJson.top.globalName;
        this.topRenderEngine.rotationVoxtorpAfterReplaceDoors(topDeafultChangeMap, needMoveUp);
        // 老的门板是沃拓普门板，新的门板是普通门板,下移门板
      } else if (oldTemplatejson?.top?.globalName === VOXTORPGLOBALNAME
        && templateJson?.top?.globalName !== VOXTORPGLOBALNAME) {
        this.topRenderEngine.downNormalDoorsAfterReplaceDoors(topDeafultChangeMap);
      }
    }

    if (skirtingBoard) {
      this.bottomRenderEngine.changeSkritingTexture(skirtingBoard);
    }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 修改台面材质
   * @param {*} templateJson json
   * @param {*} texture 材质图片地址
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeTopPanelTexture(templateJson, texture, callback) {
    this.engine.stopRenderLoop();
    this.stopRender();

    this.templateJson = templateJson;
    await this.bottomRenderEngine.changeTopPanelTexture(texture);
    this.bottomRenderEngine.templateJson = templateJson;
    if (this.topRenderEngine) { this.topRenderEngine.templateJson = templateJson; }

    await this.showAllMesh();
    this.renderSubscribe?.setHideMeshArr(templateJson);
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 修改踢脚板材质
   * @param {*} templateJson json
   * @param {*} texture 材质图片地址
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeSkirtingTexture(templateJson, texture, callback) {
    this.renderSubscribe?.setHideMeshArr(templateJson);
    this.engine.stopRenderLoop();
    this.stopRender();

    this.templateJson = templateJson;
    await this.bottomRenderEngine.changeTopPanelTexture(texture);
    this.bottomRenderEngine.templateJson = templateJson;
    if (this.topRenderEngine) { this.topRenderEngine.templateJson = templateJson; }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 改变水槽
   * @param {*} templateJson
   * @param {*} oldSink {componentBasic:xxx,relativeLocation:xxx}
   * @param {*} newSink {componentBasic:xxx,relativeLocation:xxx}
   * @param {*} edge
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeSink(templateJson, oldSink, newSink, edge, callback) {
    await this.loadAsset([newSink]);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    this.templateJson = templateJson;
    if (newSink) {
      this.bottomRenderEngine.replaceMeshes(templateJson, {
        old: [oldSink],
        new: [newSink],
      }, true);
      this.bottomRenderEngine.templateJson = templateJson;
      if (this.topRenderEngine) { this.topRenderEngine.templateJson = templateJson; }
    }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 改变水龙头
   * @param {*} templateJson
   * @param {*} oldFaucet {componentBasic:xxx,relativeLocation:xxx}
   * @param {*} newFaucet {componentBasic:xxx,relativeLocation:xxx}
   * @param {*} edge
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeFaucet(templateJson, oldFaucet, newFaucet, callback) {
    await this.loadAsset([newFaucet]);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    this.templateJson = templateJson;
    if (newFaucet) {
      this.bottomRenderEngine.replaceMeshes(templateJson, {
        old: [oldFaucet],
        new: [newFaucet],
      }, false, true);
    } else {
      this.bottomRenderEngine.removeMeshes([oldFaucet]);
    }

    this.bottomRenderEngine.templateJson = templateJson;
    if (this.topRenderEngine) {
      this.topRenderEngine.templateJson = templateJson;
    }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 改变把手
   * @param {*} templateJson
   * @param {*} bottomChangeMap 改变的把手内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @param {*} topChangeMap 改变的把手内容{old:[{componentBasic:xxx,relativeLocation:xxx}],new:[xxx]}
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeHandle(templateJson, bottomChangeMap, topChangeMap, callback) {
    this.templateJson = templateJson;
    await this.loadAsset([bottomChangeMap?.new, topChangeMap?.new]);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    if (bottomChangeMap) {
      if (bottomChangeMap?.new[0]) {
        this.bottomRenderEngine.replaceMeshes(templateJson, bottomChangeMap);
      } else {
        this.bottomRenderEngine.deleteMeshes(bottomChangeMap.old);
      }
      this.bottomRenderEngine.templateJson = templateJson;
    }
    if (topChangeMap && this.topRenderEngine) {
      if (topChangeMap?.new[0]) {
        this.topRenderEngine.replaceMeshes(templateJson, topChangeMap);
      } else {
        this.topRenderEngine.deleteMeshes(topChangeMap.old);
      }
      this.topRenderEngine.templateJson = templateJson;
    }
    await this.showAllMesh();
    if (callback) {
      callback();
    }
  }

  /**
   * @description: 改变高柜，不是新增高柜，新增高柜请调用initconfig
   * @param {*} templateJson
   * @param {*} oldHighFrame {componentBasic:xxx,relativeLocation:xxx}
   * @param {*} newHightFrame {componentBasic:xxx,relativeLocation:xxx}
   * @return {*}
   * @author: Yongchao Wang
   */
  async changeHighFrame(templateJson, oldHighFrame, newHightFrame, callback) {
    this.templateJson = templateJson;
    await this.loadAsset([newHightFrame]);
    this.renderSubscribe?.setHideMeshArr(templateJson);
    if (newHightFrame) {
      this.bottomRenderEngine.replaceMeshes(templateJson, {
        old: [oldHighFrame],
        new: [newHightFrame],
      });
      this.bottomRenderEngine.templateJson = templateJson;
      if (this.topRenderEngine) {
        this.topRenderEngine.templateJson = templateJson;
      }
    }
    await this.showAllMesh();
    if (callback) { callback(); }
  }

  // 重置相机初始位置和角度
  resetSnapshot() {
    // 重置相机视角
    const resetCamera = new ArcRotateCamera('resetCamera', 1, 1, 1, new Vector3.Zero(), this.scene);
    resetCamera.setTarget(this.initTarget);
    resetCamera.setPosition(this.initPositiont);

    const resetEngine = new Engine(this.canvas, true, {
      preserveDrawingBuffer: true, stencil: true,
    });
      // 将二字型，u型，隐藏的mesh重置显示
    this.renderSubscribe?.resetHideMeshId();

    return { resetCamera, resetEngine };
  }

  /**
   * @description: 截屏
   * @author: Yongchao Wang
   */
  async screenshot(callback) {
    await new Promise((resolve) => {
      if (this.isRendering) {
        setTimeout(() => {
          this.screenshot(callback);
        }, 100);
      } else {
        // method1
        // const { resetEngine, resetCamera } = this.resetSnapshot();
        // Tools.CreateScreenshotUsingRenderTarget(resetEngine, resetCamera, {

        // method2
        // 重置相机视角
        this.camera.setTarget(this.initTarget);
        this.camera.setPosition(this.initPositiont);
        // 将二字型，u型，隐藏的mesh重置显示
        this.renderSubscribe?.resetHideMeshId();
        // 截屏
        setTimeout(() => {
          // Tools.CreateScreenshot(this.engine, this.camera, {
          Tools.CreateScreenshotUsingRenderTarget(this.engine, this.camera, {
            // precision: devicePixelRatio, // iso15 online会白屏
          }, (data) => {
            if (callback) {
              callback({ data });
              // compressImg(data, 500, callback);
            }
            resolve();
          });
        }, 1000);
      }
    });
  }

  /**
     * @description: 监听相机位置改变
     * @param {*} callback
     * @return {*}
     * @author: Xiaohua Zhu
     */
  onCameraPositionChange(callback) {
    let [xBefore, yBefore, zBefore] = [this.camera.position.x, this.camera.position.y, this.camera.position.z];
    this.scene.afterCameraRender = () => {
      const [xAfter, yAfter, zAfter] = [this.camera.position.x, this.camera.position.y, this.camera.position.z];
      if ((xBefore !== xAfter || yBefore !== yAfter || zBefore !== zAfter) && this.showCalloutLines) {
        this.renderCalloutLines('bottom');
        this.renderCalloutLines('top');
        callback();
        [xBefore, yBefore, zBefore] = [this.camera.position.x, this.camera.position.y, this.camera.position.z];
      }
    };
  }

  checkTopHightFrameIsSeparated(lengthNum) {
    const bottomLength = Number(this.templateJson.bottom[lengthNum]?.length);
    const topLength = Number(this.templateJson.top[lengthNum].length);
    if ((topLength - bottomLength)
    - getHightFrameLengthByPosition(this.templateJson, BASICTYPE_ENUM.top, lengthNum) > TOPHIGHTFRAMESEPARATEDLENGTH) {
      return true;
    }
    return false;
  }

  // 显隐窗户
  showWindow(show) {
    this.windowVisiable = show;
    this.bottomRenderEngine.showWindow(show);
  }

  // 显隐房间
  showRoom(isShow) {
    if (this.isShowRoom !== isShow) {
      this.isShowRoom = isShow;
      if (this.bottomRenderEngine?.roomMeshes?.length === 0) {
        if (isShow) {
          this?.bottomRenderEngine?.renderRoom(this.copyTemplateJson);
        }
      } else {
        this?.bottomRenderEngine?.showWindow(isShow);
        // 墙和地面的显隐可以统一控制
        this?.bottomRenderEngine?.showRoom(isShow);
      }
    }
  }

  // 改变墙面
  changeWall(wallInfo) {
    this.bottomRenderEngine.changeWall(wallInfo);
  }

  // 改变地面
  changeGround(groundInfo) {
    this.bottomRenderEngine.changeGround(groundInfo);
  }

  callCalloutLines() {
    this.renderCalloutLines('bottom');
    this.renderCalloutLines('top');
    calloutLineRender(this.calloutLineObj);
  }

  changeCalloutLinesVisible(calloutLinesVisible) {
    this.showCalloutLines = calloutLinesVisible;
    if (!calloutLinesVisible) {
      const calloutCanvas = document.getElementById('calloutCanvas');
      const { height } = calloutCanvas;
      calloutCanvas.height = height;
    } else {
      this.callCalloutLines();
    }
  }

  // 停止渲染
  stopRender() {
    this.isRunning = false;
    this.engine.stopRenderLoop();
  }

  startRender() {
    if (this.isRunning) {
      return;
    }
    this.showAllMesh();
  }
}

export default RenderEngine;
